import { Button } from "@/components/ui/button";
import { HelpCircle, LogOut, UserCircle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { logUserAction } from "@/utils/analyticsUtils";
import { useEffect, useState } from "react";
import { NotificationsDropdown } from "@/components/notifications/NotificationsDropdown";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { signOut } from "@/lib/auth";
import { useTranslation } from "react-i18next";

interface Profile {
  id: string;
  company_name: string | null;
  profile_image_url: string | null;
  whatsapp_number: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface DashboardHeaderProps {
  profile: Profile | null;
}

export const DashboardHeader = ({
  profile
}: DashboardHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userIdentifier, setUserIdentifier] = useState<string | null>(null);

  useEffect(() => {
    // Fetch the user's email or phone when the component mounts
    const getUserInfo = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        // Try email first, then phone, in order of preference
        if (data.user.email) {
          setUserIdentifier(data.user.email);
        } else if (data.user.phone) {
          setUserIdentifier(data.user.phone);
        } else if (profile?.whatsapp_number) {
          // Fallback to profile's whatsapp number if auth doesn't have phone
          setUserIdentifier(profile.whatsapp_number);
        }
      }
    };
    
    getUserInfo();
  }, [profile]);

  const handleLogout = async () => {
    try {
      // Get user before logout to log the action
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        await logUserAction(user.id, 'user_logout');
      }
      
      const { error } = await signOut();
      if (error) {
        toast({
          variant: "destructive",
          title: t('auth.logoutError'),
          description: t('auth.failedToLogout'),
        });
      } else {
        toast({
          title: t('auth.loggedOut'),
          description: t('auth.loggedOutSuccess'),
        });
        navigate("/");
      }
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        variant: "destructive",
        title: t('auth.logoutError'),
        description: t('auth.unexpectedLogoutError'),
      });
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 bg-white z-[100]">
      <div className="flex items-center justify-between px-4 h-16">
        <div className="flex items-center gap-2">
          <img src="/lovable-uploads/bc4b57d4-e29b-4e44-8e1c-82ec09ca6fd6.png" alt="Logo" className="h-8" />
          <div className="flex items-center">
            <span className="font-bold text-xl bg-gradient-to-r from-gebeya-pink to-gebeya-orange bg-clip-text text-transparent">Jitume</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <NotificationsDropdown />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="h-9 w-9 border-2 border-gebeya-pink cursor-pointer hover:opacity-90 transition-opacity">
                <AvatarImage src={profile?.profile_image_url || undefined} alt="Profile" />
                <AvatarFallback className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white">
                  {profile?.company_name?.charAt(0).toUpperCase() || 
                   profile?.first_name?.charAt(0).toUpperCase() || 
                   profile?.last_name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel className="flex items-center gap-2">
                <UserCircle className="h-4 w-4" />
                <span className="truncate">
                  {userIdentifier || "Loading..."}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" />
                <span>{t('auth.signOut')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/* Gradient border line */}
      <div className="h-[2px] w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange"></div>
    </div>
  );
};
